/**
 * Client-side inbox queue helpers for full-page ingestion review (ITT-923).
 */

export function buildBeerIngestionReviewPath(runId, stateCode) {
  if (!runId) {
    return '/beer-ingestion?tab=review';
  }
  const params = new window.URLSearchParams();
  if (stateCode) {
    params.set('state', stateCode);
  }
  const query = params.toString();
  const path = `/beer-ingestion/review/${runId}`;
  return query ? `${path}?${query}` : path;
}

export function buildBeerIngestionInboxPath() {
  return '/beer-ingestion?tab=review';
}

export function queueRunIdsFromRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .map((row) => row?.runId)
    .filter((runId) => typeof runId === 'string' && runId.length > 0);
}

export function queueIndexForRun(runId, queueRunIds) {
  if (!runId || !Array.isArray(queueRunIds)) {
    return -1;
  }
  return queueRunIds.indexOf(runId);
}

export function resolveQueueNeighbors(runId, queueRunIds) {
  const currentIndex = queueIndexForRun(runId, queueRunIds);
  if (currentIndex < 0) {
    const firstRemaining = Array.isArray(queueRunIds) ? queueRunIds[0] : null;
    return {
      currentIndex,
      previousRunId: null,
      nextRunId: firstRemaining || null,
      positionLabel: queueRunIds?.length ? `— of ${queueRunIds.length}` : '',
    };
  }
  return {
    currentIndex,
    previousRunId: currentIndex > 0 ? queueRunIds[currentIndex - 1] : null,
    nextRunId: currentIndex < queueRunIds.length - 1
      ? queueRunIds[currentIndex + 1]
      : null,
    positionLabel: `${currentIndex + 1} of ${queueRunIds.length}`,
  };
}

/**
 * After a successful apply, walk the previous inbox order and skip ids that
 * have left the refreshed queue.
 */
export function nextQueuedRunIdAfter(appliedRunId, previousQueueIds, refreshedQueueIds) {
  const previousIds = Array.isArray(previousQueueIds) ? previousQueueIds : [];
  const refreshedIds = Array.isArray(refreshedQueueIds) ? refreshedQueueIds : [];
  const refreshedSet = new Set(refreshedIds);
  const appliedIndex = previousIds.indexOf(appliedRunId);
  const startIndex = appliedIndex >= 0 ? appliedIndex + 1 : 0;
  for (let index = startIndex; index < previousIds.length; index += 1) {
    const candidateId = previousIds[index];
    if (refreshedSet.has(candidateId)) {
      return candidateId;
    }
  }
  return null;
}

export function breweryNamesFromQueueRows(rows) {
  const names = {};
  if (!Array.isArray(rows)) {
    return names;
  }
  rows.forEach((row) => {
    if (row?.runId && row.breweryName) {
      names[row.runId] = row.breweryName;
    }
  });
  return names;
}

export function breweryNameForQueueRun(runId, rows, breweryNames) {
  if (breweryNames && runId && breweryNames[runId]) {
    return breweryNames[runId];
  }
  if (!Array.isArray(rows) || !runId) {
    return '';
  }
  const matchedRow = rows.find((row) => row.runId === runId);
  return matchedRow?.breweryName || '';
}

/**
 * Keep a previously resolved brewery name so the header never falls back to the id.
 *
 * @param {object} current - Existing runId → name map
 * @param {string} runId - Run id
 * @param {string} breweryName - Name to stash
 * @returns {object}
 */
export function mergeStashedBreweryName(current, runId, breweryName) {
  const trimmed = typeof breweryName === 'string' ? breweryName.trim() : '';
  if (!runId || !trimmed) {
    return current;
  }
  if (current?.[runId] === trimmed) {
    return current;
  }
  return { ...current, [runId]: trimmed };
}

/**
 * Prefer get-run breweryName, then the session stash, then the inbox queue.
 *
 * @param {object} params - Resolution inputs
 * @returns {string}
 */
export function resolveReviewBreweryName({
  runId,
  runBreweryName,
  detailBreweryName,
  stashedNames,
  queueRows,
  locationBreweryNames,
}) {
  const fromRun = typeof runBreweryName === 'string' ? runBreweryName.trim() : '';
  if (fromRun) {
    return fromRun;
  }
  const fromDetail = typeof detailBreweryName === 'string' ? detailBreweryName.trim() : '';
  if (fromDetail) {
    return fromDetail;
  }
  const fromStash = typeof stashedNames?.[runId] === 'string'
    ? stashedNames[runId].trim()
    : '';
  if (fromStash) {
    return fromStash;
  }
  return breweryNameForQueueRun(runId, queueRows, locationBreweryNames);
}

export function isEditableKeyboardTarget(target) {
  if (!target || typeof target !== 'object') {
    return false;
  }
  const tagName = typeof target.tagName === 'string' ? target.tagName.toUpperCase() : '';
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true;
  }
  if (target.isContentEditable) {
    return true;
  }
  const role = typeof target.getAttribute === 'function' ? target.getAttribute('role') : null;
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}
